export const PASSWORD_RESET_FLOW = 'recovery';
export const PASSWORD_RESET_PATH = '/reset-password';
export const PASSWORD_RESET_PUBLIC_ORIGIN = 'https://agendamentobonjour.lovable.app';

const PASSWORD_RESET_STORAGE_KEY = 'password-reset-recovery';

const PASSWORD_RESET_SESSION_ERROR_PATTERNS = [
  'auth session missing',
  'invalid refresh token',
  'refresh token not found',
  'session not found',
  'jwt expired',
  'one-time token',
  'link is invalid or has expired',
];

const PASSWORD_RESET_EMAIL_CLAIMS = ['email', 'user_email'] as const;

export interface RecoveryUrlState {
  accessToken: string | null;
  refreshToken: string | null;
  code: string | null;
  tokenHash: string | null;
  isRecoveryFlow: boolean;
}

export function getPasswordResetRedirectOrigin(
  origin = typeof window !== 'undefined' ? window.location.origin : PASSWORD_RESET_PUBLIC_ORIGIN,
) {
  const normalizedOrigin = origin.replace(/\/$/, '');

  if (
    normalizedOrigin.includes('localhost') ||
    normalizedOrigin.includes('127.0.0.1') ||
    normalizedOrigin.endsWith('.lovable.app')
  ) {
    return normalizedOrigin;
  }

  return PASSWORD_RESET_PUBLIC_ORIGIN;
}

export function getPasswordResetRedirectUrl(origin = getPasswordResetRedirectOrigin()) {
  return `${getPasswordResetRedirectOrigin(origin)}${PASSWORD_RESET_PATH}?flow=${PASSWORD_RESET_FLOW}`;
}

export function getRecoveryUrlState(urlString = window.location.href) {
  const url = new URL(urlString);
  const hashValue = url.hash.startsWith('#') ? url.hash.slice(1) : url.hash;
  const hashParams = new URLSearchParams(hashValue);
  const searchParams = url.searchParams;

  const accessToken = hashParams.get('access_token') ?? searchParams.get('access_token');
  const refreshToken = hashParams.get('refresh_token') ?? searchParams.get('refresh_token');
  const code = searchParams.get('code') ?? hashParams.get('code');
  const tokenHash = searchParams.get('token_hash') ?? hashParams.get('token_hash');
  const type = hashParams.get('type') ?? searchParams.get('type') ?? searchParams.get('flow');

  return {
    accessToken,
    refreshToken,
    code,
    tokenHash,
    isRecoveryFlow:
      type === PASSWORD_RESET_FLOW ||
      Boolean(accessToken) ||
      Boolean(refreshToken) ||
      Boolean(code) ||
      Boolean(tokenHash),
  } satisfies RecoveryUrlState;
}

export function hasRecoveryCredentials(state?: Partial<RecoveryUrlState> | null) {
  return Boolean(state?.accessToken || state?.refreshToken || state?.code || state?.tokenHash);
}

export function getRecoveryEmailFromAccessToken(accessToken?: string | null) {
  if (!accessToken) {
    return null;
  }

  try {
    const [, payload] = accessToken.split('.');
    if (!payload) {
      return null;
    }

    const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/');
    const paddedPayload = normalizedPayload.padEnd(Math.ceil(normalizedPayload.length / 4) * 4, '=');
    const decodedPayload = JSON.parse(window.atob(paddedPayload)) as Record<string, unknown>;

    for (const claim of PASSWORD_RESET_EMAIL_CLAIMS) {
      const value = decodedPayload[claim];
      if (typeof value === 'string' && value.length > 0) {
        return value;
      }
    }

    return null;
  } catch {
    return null;
  }
}

export function mergeRecoveryUrlState(
  primary?: Partial<RecoveryUrlState> | null,
  fallback?: Partial<RecoveryUrlState> | null,
): RecoveryUrlState {
  const merged = {
    accessToken: primary?.accessToken ?? fallback?.accessToken ?? null,
    refreshToken: primary?.refreshToken ?? fallback?.refreshToken ?? null,
    code: primary?.code ?? fallback?.code ?? null,
    tokenHash: primary?.tokenHash ?? fallback?.tokenHash ?? null,
  };

  return {
    ...merged,
    isRecoveryFlow: Boolean(primary?.isRecoveryFlow || fallback?.isRecoveryFlow || hasRecoveryCredentials(merged)),
  };
}

export function persistRecoveryUrlState(state: Partial<RecoveryUrlState>) {
  if (typeof window === 'undefined' || !hasRecoveryCredentials(state)) {
    return;
  }

  window.sessionStorage.setItem(
    PASSWORD_RESET_STORAGE_KEY,
    JSON.stringify({
      accessToken: state.accessToken ?? null,
      refreshToken: state.refreshToken ?? null,
      code: state.code ?? null,
      tokenHash: state.tokenHash ?? null,
    }),
  );
}

export function readPersistedRecoveryUrlState(): RecoveryUrlState | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const rawState = window.sessionStorage.getItem(PASSWORD_RESET_STORAGE_KEY);
  if (!rawState) {
    return null;
  }

  try {
    const parsed = JSON.parse(rawState) as Partial<RecoveryUrlState>;
    return mergeRecoveryUrlState(parsed, { isRecoveryFlow: hasRecoveryCredentials(parsed) });
  } catch {
    return null;
  }
}

export function clearPersistedRecoveryUrlState() {
  if (typeof window === 'undefined') {
    return;
  }

  window.sessionStorage.removeItem(PASSWORD_RESET_STORAGE_KEY);
}

export function getPasswordResetCleanupUrl(urlString = window.location.href) {
  const url = new URL(urlString);
  return `${url.pathname}?flow=${PASSWORD_RESET_FLOW}`;
}

export function isPasswordResetSessionError(message?: string | null) {
  const normalizedMessage = message?.toLowerCase() ?? '';
  return PASSWORD_RESET_SESSION_ERROR_PATTERNS.some((pattern) => normalizedMessage.includes(pattern));
}